// askSmart-ui/src/pages/TOSP/components/LeaseLapsedModal.tsx
import React from "react";
import { formatDate } from "../format";
import { formatSpan, LapsedTenancy, spanBetween, tenantLabel, todayISO } from "../types";

interface LeaseLapsedModalProps {
  lapsed: LapsedTenancy | null;
  /** How many are still waiting behind this one, for the queue line. */
  remaining: number;
  /** Move them out: records the exit date as the lease's last day. */
  onConfirm: () => void | Promise<void>;
  /** Leave them be: the renewal is late, not the tenancy over. */
  onCancel: () => void;
  busy?: boolean;
}

// Asked when a lease has run out and nobody has ruled on it.
//
// The question is deliberately the one an admin can answer from memory — have
// they left? — rather than asking them to go and set a date. Everything else
// follows from the answer: the exit date, the tab they appear on, whether the
// unit is free.
//
// Cancel is a real answer, not a dismissal. It means the renewal is late and the
// tenant is still trading, so nothing is written and they stay exactly as they
// are. It only stops the ASKING, and the traffic light keeps the question open.
export default function LeaseLapsedModal({
  lapsed,
  remaining,
  onConfirm,
  onCancel,
  busy = false,
}: LeaseLapsedModalProps) {
  if (!lapsed) return null;

  const name = tenantLabel(lapsed.tenant) || "This tenant";
  const over = spanBetween(lapsed.endedOn, todayISO());

  // Today reads differently from a backlog: "zero days remaining" is a decision
  // due now, "ended 127 days ago" is one already overdue. Same question, but an
  // admin reading the second needs to know how long it has been sitting there.
  const headline =
    lapsed.daysOver === 0
      ? `${name} has zero days remaining on their current lease.`
      : `${name}'s lease ended ${over ? formatSpan(over) : `${lapsed.daysOver} days`} ago.`;

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal" style={{ width: "520px" }}>
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>Lease Ended</h2>
        </div>
        <div className="smrtdocs-modal-body">
          <p style={{ fontSize: "0.9rem", lineHeight: 1.5, margin: "0 0 12px" }}>
            {headline} Do you want to move them to Past Tenants now?
          </p>

          <p className="tosp-field-hint" style={{ margin: "0 0 16px" }}>
            Their exit date will be recorded as{" "}
            <strong>{formatDate(lapsed.endedOn)}</strong>, the last day of the lease,
            and the unit they held becomes available. If the renewal is simply
            outstanding, choose Not Yet and nothing changes.
          </p>

          {remaining > 0 && (
            <p className="tosp-field-hint" style={{ margin: "0 0 16px" }}>
              {remaining} other {remaining === 1 ? "lease has" : "leases have"} also run
              out — {remaining === 1 ? "it" : "they"} will be asked about next.
            </p>
          )}

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button
              className="btn-cancel"
              onClick={onCancel}
              disabled={busy}
              style={{ opacity: busy ? 0.5 : 1, cursor: busy ? "not-allowed" : "pointer" }}
            >
              Not Yet
            </button>
            <button
              className="btn-save"
              onClick={() => void onConfirm()}
              disabled={busy}
              style={{
                opacity: busy ? 0.7 : 1,
                cursor: busy ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {busy && <span className="spinner" />}
              Move to Past Tenants
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
